/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useMemo } from 'react';
import servicioService from '../services/servicioService';
import { toast } from 'react-toastify';

const Servicios = () => {
  const [servicios, setServicios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Estados para paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalServicios, setTotalServicios] = useState(0);
  const pageSize = 10;

  // Estados para filtros
  const [filtros, setFiltros] = useState({
    fromDate: '',
    toDate: '',
    userName: '',
    companyName: '',
    serviceName: '',
    search: ''
  });

  // Memorizar las dependencias para evitar cambios de tamaño en useEffect
  const filtrosDependencias = useMemo(() => {
    return JSON.stringify(filtros);
  }, [filtros]);

  // Cargar servicios cuando cambie la página o filtros
  useEffect(() => {
    fetchServicios();
  }, [currentPage, filtrosDependencias]);

  // Función para obtener servicios con paginación y filtros
  const fetchServicios = async () => {
    try {
      setLoading(true);

      // Usar el nuevo endpoint optimizado con paginación y filtros
      const response = await servicioService.getServiciosFiltered(currentPage, pageSize, filtros);
      console.log('Datos recibidos del backend:', response);

      // El endpoint ya devuelve solo servicios filtrados
      const serviciosData = response.data || response.services || response.serviceRequests || [];

      // Actualizar información de paginación
      const totalPagesFromResponse = response.totalPages || response.pagination?.totalPages || Math.ceil((response.total || response.pagination?.total || 0) / pageSize);
      const totalServiciosFromResponse = response.total || response.pagination?.total || response.count || 0;

      console.log('Información de paginación extraída:', {
        totalPages: totalPagesFromResponse,
        totalServicios: totalServiciosFromResponse,
        currentPage: currentPage
      });

      setTotalPages(totalPagesFromResponse);
      setTotalServicios(totalServiciosFromResponse);

      setServicios(Array.isArray(serviciosData) ? serviciosData : []);
      setError(null);
    } catch (err) {
      console.error('Error al cargar servicios:', err);
      setError('No se pudieron cargar los servicios. Por favor, intenta de nuevo más tarde.');
      toast.error('Error al cargar servicios');
    } finally {
      setLoading(false);
    }
  };

  // Funciones de navegación de paginación
  const goToFirstPage = () => {
    setCurrentPage(1);
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const goToLastPage = () => {
    setCurrentPage(totalPages);
  };

  // Función para manejar cambios en los filtros
  const handleFiltroChange = (campo, valor) => {
    setFiltros(prev => ({
      ...prev,
      [campo]: valor
    }));
    setCurrentPage(1); // Resetear a la primera página al cambiar filtros
  };

  // Función para limpiar filtros
  const limpiarFiltros = () => {
    setFiltros({
      fromDate: '',
      toDate: '',
      userName: '',
      companyName: '',
      serviceName: '',
      search: ''
    });
    setCurrentPage(1);
  };

  // Función para eliminar un servicio
  const handleDeleteServicio = async (id) => {
    if (window.confirm('¿Estás seguro de que deseas eliminar este servicio?')) {
      try {
        setLoading(true);
        await servicioService.deleteServicio(id);
        setServicios(servicios.filter(servicio => servicio.id !== id));
        toast.success('Servicio eliminado exitosamente');
      } catch (err) {
        console.error('Error al eliminar servicio:', err);
        toast.error('Error al eliminar servicio');
      } finally {
        setLoading(false);
      }
    }
  };

  // Función para formatear la fecha
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';

    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch (error) {
      console.error('Error al formatear fecha:', error);
      return 'Fecha inválida';
    }
  };

  // Placeholder para imagen de perfil
  const defaultProfileImage = `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' width='40' height='40'%3E%3Cpath fill='%23ccc' d='M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z'/%3E%3C/svg%3E`;

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold mb-4">Gestión de Servicios</h2>
      <div className="bg-white rounded-lg shadow-md p-6">
        <p className="text-gray-600 mb-4">
          Administra los servicios registrados en la plataforma.
        </p>

        {/* Filtros */}
        <div className="mt-6 bg-gray-50 p-4 rounded-lg">
          <h4 className="text-lg font-medium text-gray-700 mb-4">Filtros</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Filtro de búsqueda general */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Búsqueda general
              </label>
              <input
                type="text"
                placeholder="Buscar en todos los campos..."
                value={filtros.search}
                onChange={(e) => handleFiltroChange('search', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Filtro por nombre de usuario */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nombre de usuario
              </label>
              <input
                type="text"
                placeholder="Buscar por usuario..."
                value={filtros.userName}
                onChange={(e) => handleFiltroChange('userName', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Filtro por nombre de empresa */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nombre de empresa
              </label>
              <input
                type="text"
                placeholder="Buscar por empresa..."
                value={filtros.companyName}
                onChange={(e) => handleFiltroChange('companyName', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Filtro por nombre de servicio */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nombre de servicio
              </label>
              <input
                type="text"
                placeholder="Buscar por servicio..."
                value={filtros.serviceName}
                onChange={(e) => handleFiltroChange('serviceName', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Filtro por fecha desde */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Fecha desde
              </label>
              <input
                type="date"
                value={filtros.fromDate}
                onChange={(e) => handleFiltroChange('fromDate', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Filtro por fecha hasta */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Fecha hasta
              </label>
              <input
                type="date"
                value={filtros.toDate}
                onChange={(e) => handleFiltroChange('toDate', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Botón para limpiar filtros */}
          <div className="mt-4">
            <button
              onClick={limpiarFiltros}
              className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-md text-sm"
            >
              Limpiar filtros
            </button>
          </div>
        </div>

        {/* Estado de carga y error */}
        {loading && <p className="mt-4 text-gray-600">Cargando servicios...</p>}
        {error && <p className="mt-4 text-red-500">{error}</p>}

        {/* Tabla de servicios */}
        {!loading && !error && (
          <div className="overflow-x-auto mt-6">
            <table className="min-w-full bg-white">
              <thead>
                <tr className="w-full h-16 border-gray-300 border-b py-8">
                  <th className="text-left pl-6 font-bold text-gray-600">Usuario</th>
                  <th className="text-left font-bold text-gray-600">Empresa</th>
                  <th className="text-left font-bold text-gray-600">Servicio</th>
                  <th className="text-left font-bold text-gray-600">Fecha</th>
                  <th className="text-center font-bold text-gray-600">Número de Servicios</th>
                  <th className="text-center font-bold text-gray-600">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {servicios.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="text-center py-6 text-gray-500">
                      No hay servicios registrados.
                    </td>
                  </tr>
                ) : (
                  servicios.map(servicio => (
                    <tr key={servicio.id} className="h-16 border-gray-300 border-b hover:bg-gray-50">
                      <td className="py-3 px-6 text-left">
                        <div className="flex items-center">

                          <span>{servicio.userName || 'Usuario desconocido'}</span>
                        </div>
                      </td>
                      <td className="py-3 px-6 text-left">{servicio.companyName || 'Sin empresa'}</td>
                      <td className="py-3 px-6 text-left">{servicio.serviceTitle || 'Sin nombre'}</td>
                      <td className="py-3 px-6 text-left">{formatDate(servicio.serviceDate || servicio.date)}</td>
                      <td className="py-3 px-6 text-center">{servicio.serviceCount || 0}</td>
                      <td className="py-3 px-6 text-center">
                        <div className="flex item-center justify-center">
                          <button
                            onClick={() => handleDeleteServicio(servicio.id)}
                            className="text-red-500 hover:text-red-700 mx-1"
                          >
                            Eliminar
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Controles de paginación */}
        {!loading && !error && totalPages > 0 && (
          <div className="mt-6 flex items-center justify-between">
            <div className="text-sm text-gray-700">
              Mostrando página {currentPage} de {totalPages} ({totalServicios} servicios en total)
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={goToFirstPage}
                disabled={currentPage === 1}
                className={`px-3 py-1 rounded ${currentPage === 1
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-blue-500 text-white hover:bg-blue-600'
                  }`}
              >
                ««
              </button>
              <button
                onClick={goToPreviousPage}
                disabled={currentPage === 1}
                className={`px-3 py-1 rounded ${currentPage === 1
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-blue-500 text-white hover:bg-blue-600'
                  }`}
              >
                ‹ Anterior
              </button>
              <span className="px-3 py-1 bg-gray-100 rounded">
                {currentPage} / {totalPages}
              </span>
              <button
                onClick={goToNextPage}
                disabled={currentPage === totalPages}
                className={`px-3 py-1 rounded ${currentPage === totalPages
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-blue-500 text-white hover:bg-blue-600'
                  }`}
              >
                Siguiente ›
              </button>
              <button
                onClick={goToLastPage}
                disabled={currentPage === totalPages}
                className={`px-3 py-1 rounded ${currentPage === totalPages
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-blue-500 text-white hover:bg-blue-600'
                  }`}
              >
                »»
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Servicios;
